import posthogJs from 'posthog-js';
import { appConfig } from 'config';
import { sessionRecordingOptions, stripQuery, toSampleRate } from 'posthog-privacy';
const { posthogKey, posthogHost, posthogSessionReplay, posthogSessionReplaySampleRate, env } = appConfig;
/**
 * Filter state rides in the query string, so no URL property may keep its
 * query or fragment. `utm_*` attribution is lifted out before this runs.
 */
const scrubUrlProperties = (properties) => {
    if (!properties) {
        return;
    }
    for (const key of Object.keys(properties)) {
        const value = properties[key];
        if (typeof value === 'string' && /(?:url|referrer)$/iu.test(key)) {
            properties[key] = stripQuery(value);
        }
    }
};
let accountProperties = {};
/**
 * Support admins switch which account they are viewing, so account context has
 * to ride on each event. As a person property it would be last-write-wins and
 * would relabel every event that person had already sent.
 */
export const setPosthogAccountContext = (properties) => {
    accountProperties = properties;
};
export const beforeSend = (event) => {
    // Replay batches are masked by `sessionRecordingOptions` and need no context.
    if (!event || event.event === '$snapshot') {
        return event;
    }
    scrubUrlProperties(event.properties);
    scrubUrlProperties(event.$set);
    scrubUrlProperties(event.$set_once);
    // `environment` mirrors the `env` tag Datadog RUM sets. Each environment
    // reports into its own project, so it only disambiguates where two share one.
    // Stamped per event rather than via `register`, whose store `reset()` clears.
    event.properties = {
        ...accountProperties,
        ...event.properties,
        environment: env
    };
    return event;
};
if (posthogKey) {
    posthogJs.init(posthogKey, {
        // posthog-js already points at US Cloud; only override when configured.
        ...(posthogHost ? { api_host: posthogHost } : {}),
        defaults: '2026-01-30',
        before_send: beforeSend,
        // Every event here is captured explicitly, and autocapture would ship the
        // clicked element's text — candidate names, emails, phone numbers.
        autocapture: false,
        // Per-environment opt-in so stage can verify masking before prod.
        disable_session_recording: !posthogSessionReplay,
        session_recording: {
            ...sessionRecordingOptions,
            sampleRate: toSampleRate(posthogSessionReplaySampleRate)
        },
        // Console output can include API payloads.
        enable_recording_console_log: false,
        // Neither follows `autocapture`; left unset they fall through to the
        // project's remote config. Heatmap buffers are keyed by the raw href, which
        // `beforeSend` cannot reach, and dead clicks carry element text.
        capture_heatmaps: false,
        capture_dead_clicks: false
    });
}
else if (env === 'development') {
    // Deployed environments may leave PostHog unconfigured on purpose, so only
    // a local run is worth warning about.
    console.warn('REACT_APP_POSTHOG_KEY unset — PostHog events are dropped. See .env.example.');
}
/**
 * Every posthog-js call is a no-op before `init` — the "you must initialize"
 * warnings sit behind `POSTHOG_DEBUG` — so these guards only spare the work in
 * environments such as `prod-eu` that ship without a key. Raw `capture` and
 * `captureException` need no equivalent.
 */
export const identifyPosthogUser = (distinctId, properties) => {
    if (posthogKey) {
        posthogJs.identify(distinctId, properties);
    }
};
export const resetPosthogUser = () => {
    if (posthogKey) {
        posthogJs.reset();
    }
};
export { default as posthog } from 'posthog-js';
